import React from 'react';
import type { DatabaseResponse } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { getDatabaseTypeMeta, DATABASE_STATUS_LABEL } from './databaseTypeMeta';

/**
 * Card de uma base de dados (view "Cards" da galeria). Padrão-lista: menu ••• de
 * ações (Abrir / Configurações / Excluir). Card inteiro clicável abre a base.
 * Sem checkbox (F34: seleção em massa só na view Lista).
 */
export interface DatabaseGalleryCardProps {
  database: DatabaseResponse;
  onOpen: (id: string) => void;
  onSettings?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export const DatabaseGalleryCard: React.FC<DatabaseGalleryCardProps> = ({
  database,
  onOpen,
  onSettings,
  onDelete,
}) => {
  const meta = getDatabaseTypeMeta(database.type);
  const count = database.totalDocuments ?? 0;
  const isActive = database.status === 'active';

  const actions: CardAction[] = [
    { label: 'Abrir', icon: 'external-link', onClick: () => onOpen(database.id) },
  ];
  if (onSettings) actions.push({ label: 'Configurações', icon: 'settings', onClick: () => onSettings(database.id) });
  if (onDelete) actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(database.id), variant: 'danger' });

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(database.id)}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(database.id); }}
      className="group relative rounded-xl border p-3.5 flex flex-col gap-3 cursor-pointer transition-colors"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
      data-testid={`database-card-${database.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div
          className="w-11 h-11 rounded-[11px] flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'var(--theme-primary-background)' }}
        >
          <Icon name={meta.icon} className="w-[22px] h-[22px]" style={{ color: 'var(--theme-primary)' }} />
        </div>
        <div className="flex-shrink-0 -mr-1 -mt-1" onClick={(e) => e.stopPropagation()}>
          <CardActionsMenu actions={actions} size="sm" data-testid={`database-actions-${database.id}`} />
        </div>
      </div>

      <div className="min-w-0">
        <h3 className="font-bold text-[15px] truncate" style={{ color: 'var(--theme-card-text)' }}>
          {database.name}
        </h3>
        <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>{meta.label}</p>
        {database.description && (
          <p className="text-xs mt-1 line-clamp-2" style={{ color: 'var(--theme-muted-text)' }}>
            {database.description}
          </p>
        )}
      </div>

      {/* Footer meta: contagem + integração + status (se não ativa) */}
      <div className="flex items-center flex-wrap gap-2 text-xs" style={{ color: 'var(--theme-muted-text)' }}>
        <span>
          <span className="font-bold tabular-nums" style={{ color: 'var(--theme-card-text)' }}>{count}</span>{' '}
          {count === 1 ? 'registro' : 'registros'}
        </span>
        {database.integrationId && (
          <span
            className="inline-flex items-center gap-1 h-[22px] px-2 rounded-full font-semibold"
            style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
          >
            <Icon name="link" className="w-3 h-3" />
            Integração
          </span>
        )}
        {!isActive && (
          <span
            className="inline-flex items-center gap-1.5 h-[22px] px-2 rounded-full font-semibold"
            style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
          >
            {DATABASE_STATUS_LABEL[database.status] ?? database.status}
          </span>
        )}
      </div>
    </div>
  );
};

DatabaseGalleryCard.displayName = 'DatabaseGalleryCard';
